import React, { useEffect, useState, useCallback } from "react";
import { Search, UserPlus, Pencil, Ban, Unlock, Eye, Copy } from "lucide-react";
import { toast } from "sonner";
import { adminUsers, adminUpdateMember, apiError } from "../../lib/adminApi";
import { PlanBadge, OriginBadge, fmtDate, fmtAgo, inputCls, btnPrimary, btnGhost, Empty } from "./ui";
import MemberDialog from "./MemberDialog";
import UserDetail from "./UserDetail";

export default function AdminUsers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [plan, setPlan] = useState("");
  const [origin, setOrigin] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // null | "new" | user
  const [detail, setDetail] = useState(null);
  const [created, setCreated] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await adminUsers({ q, plan, origin })).users); } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [q, plan, origin]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const toggleDisabled = async (u) => {
    try {
      await adminUpdateMember(u.user_id, { disabled: !u.disabled });
      toast.success(u.disabled ? "Conta desbloqueada" : "Conta bloqueada");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const copy = (text) => { navigator.clipboard?.writeText(text); toast.success("Copiado"); };

  return (
    <div className="space-y-5" data-testid="admin-users">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Usuários & membros-fundadores</h1>
          <p className="text-[13px] text-slate-400 mt-1">{rows.length} contas · crie, edite planos e validade, redefina senhas e bloqueie acessos.</p>
        </div>
        <button onClick={() => setDialog("new")} className={btnPrimary} data-testid="new-member-btn"><UserPlus size={15} /> Novo membro</button>
      </div>

      {created && (
        <div className="rounded-2xl bg-emerald-500/10 ring-1 ring-emerald-400/30 p-4 text-[13px]" data-testid="created-banner">
          <div className="font-semibold text-emerald-200">Membro criado. Envie estes dados de acesso:</div>
          <div className="mt-2 flex flex-wrap items-center gap-3 font-mono-jb text-slate-100">
            <span>{created.email}</span><span className="text-slate-500">·</span><span>{created.password}</span>
            <button onClick={() => copy(`Acesse https://app.basiqorcamentos.com.br\nE-mail: ${created.email}\nSenha: ${created.password}`)} className={`${btnGhost} h-8 px-3`} data-testid="copy-credentials"><Copy size={13} /> Copiar</button>
            <button onClick={() => setCreated(null)} className="text-slate-400 hover:text-white text-[12px]">fechar</button>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome ou e-mail" className={`${inputCls} pl-9`} data-testid="users-search" />
        </div>
        <select value={plan} onChange={(e) => setPlan(e.target.value)} className={`${inputCls} sm:w-40`} data-testid="users-filter-plan">
          <option value="">Todos os planos</option><option value="free">Free</option><option value="standard">Standard</option><option value="pro">Pro</option>
        </select>
        <select value={origin} onChange={(e) => setOrigin(e.target.value)} className={`${inputCls} sm:w-40`} data-testid="users-filter-origin">
          <option value="">Toda origem</option><option value="stripe">Stripe</option><option value="cortesia">Cortesia</option><option value="free">Free</option>
        </select>
      </div>

      <div className="rounded-2xl ring-1 ring-white/[0.06] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="users-table">
            <thead className="bg-white/[0.03] text-[10.5px] uppercase tracking-wider text-slate-400">
              <tr>
                {["Usuário", "Plano", "Origem", "Expira", "Cadastro", "Último acesso", "Dias ativos", "Orçamentos", "Empresas", "WhatsApp", ""].map((h) => <th key={h} className="text-left font-semibold px-3 py-2.5 whitespace-nowrap">{h}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.05]">
              {rows.map((u) => (
                <tr key={u.user_id} className={`hover:bg-white/[0.02] ${u.disabled ? "opacity-50" : ""}`} data-testid={`user-row-${u.email}`}>
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-slate-100 flex items-center gap-2">{u.name || "—"}{u.disabled && <span className="text-[10px] px-1.5 rounded bg-rose-500/20 text-rose-300">bloqueado</span>}</div>
                    <div className="text-slate-500">{u.email}</div>
                  </td>
                  <td className="px-3 py-2.5"><PlanBadge plan={u.effective_plan} testid={`plan-${u.email}`} /></td>
                  <td className="px-3 py-2.5"><OriginBadge origin={u.origin} /></td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-slate-300">{u.plan_expires_at ? fmtDate(u.plan_expires_at) : "—"}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-slate-400">{fmtDate(u.created_at)}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-slate-400">{fmtAgo(u.last_seen_at)}</td>
                  <td className="px-3 py-2.5 font-mono-jb text-center">{u.usage.active_days_30d}</td>
                  <td className="px-3 py-2.5 font-mono-jb text-center">{u.usage.quotes}<span className="text-slate-500"> ({u.usage.quotes_30d})</span></td>
                  <td className="px-3 py-2.5 font-mono-jb text-center">{u.usage.companies}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{u.usage.wa_phones.length ? <span className="text-emerald-300">{u.usage.wa_phones.length} nº · {u.usage.wa_msgs_30d} msgs</span> : <span className="text-slate-600">—</span>}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => setDetail(u.user_id)} title="Detalhes" className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5" data-testid={`view-${u.email}`}><Eye size={15} /></button>
                      <button onClick={() => setDialog(u)} title="Editar" className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5" data-testid={`edit-${u.email}`}><Pencil size={15} /></button>
                      <button onClick={() => toggleDisabled(u)} title={u.disabled ? "Desbloquear" : "Bloquear"} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-300 hover:bg-white/5" data-testid={`block-${u.email}`}>{u.disabled ? <Unlock size={15} /> : <Ban size={15} />}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && rows.length === 0 && <Empty text="Nenhum usuário encontrado" />}
      </div>

      {dialog && (
        <MemberDialog
          user={dialog === "new" ? null : dialog}
          onClose={() => setDialog(null)}
          onSaved={(u, creds) => { setDialog(null); if (creds) setCreated(creds); load(); }}
        />
      )}
      {detail && <UserDetail userId={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}
